import { Registration } from 'src/app/models/registration';
import { SoldPlate } from 'src/app/models/soldPlate';
import { excludeRegtransfers } from '../dateless-register/register-feed';

/* =============================================================================
   Initials live-feed helpers. Pure functions, unit-tested.

   Verified against the live plato-search feed (2026-07-23, HS and JW):
     · each style bucket returns the COMPLETE register range for the format
       (dateless: 9,999 rows for 1-2 letter initials, 999 for 3; prefix/suffix
       1,386; current 51), advertised or not
     · price is in pence; -1 marks a registration that is not advertised
     · seller carries the listing source (DVLA, RegPlates, ...), the field the
       Regtransfers exclusion runs on

   HARD RULE (build pack §4, acceptance i): Regtransfers-derived listings,
   prices and source labels never appear on any public surface. The exclusion
   reuses the register pages' tested helper and runs on every feed ingest.
   ========================================================================== */

/** POA sentinel, the register-page convention (dateless component). */
export const POA_SENTINEL = 1000000000;

/** Advertised = carried with a non-negative price by a live source. */
export function isAdvertised(row: Registration): boolean {
  const p = row && (row as any).price;
  return typeof p === 'number' && p >= 0;
}

/** True when the row carries a real displayable price (not POA, not free-0). */
export function hasDisplayPrice(row: Registration): boolean {
  const p = row && (row as any).price;
  return typeof p === 'number' && p > 0 && p < POA_SENTINEL;
}

/** Feed ingest: the advertised subset of a bucket, Regtransfers excluded. */
export function advertisedRows(rows: Registration[]): Registration[] {
  return excludeRegtransfers(rows || []).filter(isAdvertised);
}

/* ---- registry status (Gate 3 status rule; the feed proves only whether a
   registration is advertised right now, nothing else) ---- */

export type RowStatus = 'available' | 'unadvertised';

/** price >= 0 from a permitted seller = Available now; price < 0 = Not
    currently advertised. No off-market/ownership/last-seen inference. */
export function rowStatus(row: Registration): RowStatus {
  return isAdvertised(row) ? 'available' : 'unadvertised';
}

/** Explorer ordering: advertised rows by the chosen sort, then unadvertised
    rows in register order. Unadvertised rows carry no price, so price sorts
    must never interleave them with priced rows. */
export function compareRows(sort: 'price-asc' | 'price-desc' | 'reg-az') {
  return (a: Registration, b: Registration): number => {
    const aAv = isAdvertised(a) ? 0 : 1;
    const bAv = isAdvertised(b) ? 0 : 1;
    if (aAv !== bAv) return aAv - bAv;
    if (aAv === 1 || sort === 'reg-az') {
      return (a.registration || '').localeCompare(b.registration || '', 'en', { numeric: true });
    }
    const ap = (a as any).price || 0;
    const bp = (b as any).price || 0;
    return sort === 'price-asc' ? ap - bp : bp - ap;
  };
}

/* ---- verified prior sales (Gate 3 §6) ---- */

/** Genuine sale types, the shipped Number Ones convention. Live-listing
    pseudo-entries (saleType 4) are excluded by construction. */
export const GENUINE_SALE_TYPES = [0, 3, 6, 7, 10, 100];

export interface VerifiedSale {
  pricePence: number;
  year: number;
  date: Date;
}

/* ---- registration lookup (Gate 3 §5) ---- */

/** Lookup term to canonical plate-page registration. Digits complete the
    initials (letter-first convention); anything containing a letter is
    treated as a complete registration. Returns null when nothing valid can
    be built (no digits survives sanitising). */
export function buildLookupReg(initials: string, term: string): string | null {
  const raw = (term || '').trim().toUpperCase();
  if (!raw) return null;
  const reg = /[A-Z]/.test(raw)
    ? raw.replace(/[^A-Z0-9]/g, '')
    : ((initials || '') + raw.replace(/[^0-9]/g, '')).replace(/\s+/g, '').toUpperCase();
  if (!reg || !/[0-9]/.test(reg) || !/[A-Z]/.test(reg)) return null;
  return reg;
}

/** Latest genuine sale from a plate's recorded history, or null. */
export function latestGenuineSale(sales: SoldPlate[]): VerifiedSale | null {
  let best: VerifiedSale | null = null;
  for (const s of sales || []) {
    if (GENUINE_SALE_TYPES.indexOf(Number((s as any).saleType)) === -1) continue;
    const d = new Date(s.sold_date as any);
    if (isNaN(d.getTime())) continue;
    const pricePence = Number(s.sold_price);
    if (!pricePence || pricePence <= 0) continue;
    if (!best || d.getTime() > best.date.getTime()) {
      best = { pricePence, year: d.getFullYear(), date: d };
    }
  }
  return best;
}

export interface PriceExtreme {
  registration: Registration;
  pricePence: number;
}

/** Client-side min/max advertised price across buckets (acceptance b).
    Only rows with a real display price count; POA rows cannot set either. */
export function priceExtremes(rows: Registration[]): { lowest: PriceExtreme | null; highest: PriceExtreme | null } {
  let lowest: PriceExtreme | null = null;
  let highest: PriceExtreme | null = null;
  for (const row of rows || []) {
    if (!hasDisplayPrice(row)) continue;
    const p = (row as any).price as number;
    if (!lowest || p < lowest.pricePence) lowest = { registration: row, pricePence: p };
    if (!highest || p > highest.pricePence) highest = { registration: row, pricePence: p };
  }
  return { lowest, highest };
}

/* ---- budget bands (prototype labels; thresholds in pence) ---- */

export interface BudgetBand {
  label: string;
  loPence: number;      // inclusive
  hiPence: number;      // exclusive
}

export const BUDGET_BANDS: BudgetBand[] = [
  { label: 'Under £500', loPence: 0, hiPence: 50000 },
  { label: '£500 to £1,000', loPence: 50000, hiPence: 100000 },
  { label: '£1,000 to £5,000', loPence: 100000, hiPence: 500000 },
  { label: '£5,000 to £20,000', loPence: 500000, hiPence: 2000000 },
  { label: '£20,000 and up', loPence: 2000000, hiPence: Number.MAX_SAFE_INTEGER },
];

export interface BandResult {
  band: BudgetBand;
  /** Cheapest advertised registration in the band, or null when none. */
  pick: Registration | null;
  /** Which bucket the pick came from (display label). */
  pickFormat: string | null;
  /** How many advertised registrations fall in the band (all buckets). */
  count: number;
}

/** Real budget ladder: cheapest advertised item per band from the live
    buckets (acceptance: real registration + current price per band;
    Regtransfers excluded upstream by advertisedRows). */
export function buildBands(
  buckets: { formatLabel: string; rows: Registration[] }[]
): BandResult[] {
  return BUDGET_BANDS.map((band) => {
    let pick: Registration | null = null;
    let pickFormat: string | null = null;
    let count = 0;
    for (const bucket of buckets || []) {
      for (const row of bucket.rows || []) {
        if (!hasDisplayPrice(row)) continue;
        const p = (row as any).price as number;
        if (p < band.loPence || p >= band.hiPence) continue;
        count++;
        if (!pick || p < (pick as any).price) {
          pick = row;
          pickFormat = bucket.formatLabel;
        }
      }
    }
    return { band, pick, pickFormat, count };
  });
}
